/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type ContentStackDealsSplit } from '../../interface';
import { Grid } from '@/components/Grid';
import { type Props as HeadlineProps } from '@/components/Headline/Headline';
import { CMSHeadline } from '../CMSHeadline/CMSHeadline';
import { CMSImage } from '../CMSImage/CMSImage';
import { CMSLink } from '../CMSLink/CMSLink';
import styles from './CMSDealsSplit.module.scss';
import { Legal } from '@/components/AZCustomComponent/Legal';
import { mapContentstackLegalValues } from '@/utils/mapContentstackLegalValues';
import cx from 'classnames';
import { type CSSProperties } from 'react';

type Props = {
  content: ContentStackDealsSplit;
  headlineOptions?: HeadlineProps;
  priority?: boolean;
};

export function CMSDealsSplit({ content, headlineOptions, priority = false }: Props) {
  const { heading, underline, background_color, text_color, image_alignment, deal_tiles, layout } =
    content;

  if (!deal_tiles || deal_tiles.length === 0) {
    return null;
  }

  const isImageOverlap = layout === 'Image Overlap';
  const isImageTop = layout === 'Image Top';

  return (
    <Grid container spacing={0} data-testid="deals-split">
      {heading && (
        <CMSHeadline {...headlineOptions} underline={underline}>
          {heading}
        </CMSHeadline>
      )}
      <div className={styles.dealTilesContainer}>
        <ul className={styles.dealTilesList}>
          {deal_tiles.map((tile, i) => (
            <li
              key={i}
              className={cx(styles.dealTileItem, {
                [styles.imageOverlapLayout]: isImageOverlap,
                [styles.imageTopItem]: isImageTop,
              })}
            >
              <div className={styles.dealTileContainer}>
                {tile.cta_link ? (
                  <CMSLink
                    to={tile.cta_link.href}
                    className={styles.tileLink}
                    aria-label={`${tile.cta_link.title}${tile.description ? ` - ${tile.description}` : ''}`}
                  >
                    <div
                      className={cx(styles.dealTile, {
                        [styles.imageOverlapLayoutItem]: isImageOverlap,
                        [styles.imageTopLayoutItem]: isImageTop,
                      })}
                      style={
                        {
                          flexDirection: isImageTop
                            ? 'column'
                            : image_alignment === 'Right'
                              ? 'row-reverse'
                              : 'row',
                          backgroundColor: isImageOverlap
                            ? 'transparent'
                            : background_color?.hex || 'inherit',
                          color: text_color?.hex || 'inherit',
                          '--deal-tile-background-color': background_color?.hex || '#fff',
                        } as CSSProperties
                      }
                    >
                      {tile.image && (
                        <div className={styles.imageContainer}>
                          <CMSImage
                            image={tile.image}
                            priority={i === 0 ? priority : undefined}
                            loading={i !== 0 && priority ? 'eager' : undefined}
                          />
                        </div>
                      )}
                      <div
                        className={cx(styles.contentContainer, {
                          [styles.imageOverlapContentImageRight]:
                            isImageOverlap && image_alignment === 'Right',
                          [styles.imageOverlapContentImageLeft]:
                            isImageOverlap && image_alignment !== 'Right',
                        })}
                      >
                        {tile.heading && <h3 className={styles.tileHeading}>{tile.heading}</h3>}
                        {tile.description && (
                          <p className={styles.tileDescription}>{tile.description}</p>
                        )}
                        {tile.cta_link && (
                          <span
                            className={styles.ctaLink}
                            style={{ color: text_color?.hex || 'inherit' }}
                          >
                            {tile.cta_link.title}
                          </span>
                        )}
                      </div>
                    </div>
                  </CMSLink>
                ) : (
                  <div
                    className={cx(styles.dealTile, {
                      [styles.imageOverlapLayoutItem]: isImageOverlap,
                      [styles.imageTopLayoutItem]: isImageTop,
                    })}
                    style={
                      {
                        flexDirection: isImageTop
                          ? 'column'
                          : image_alignment === 'Right'
                            ? 'row-reverse'
                            : 'row',
                        backgroundColor: isImageOverlap
                          ? 'transparent'
                          : background_color?.hex || 'inherit',
                        color: text_color?.hex || 'inherit',
                        '--deal-tile-background-color': background_color?.hex || '#fff',
                      } as CSSProperties
                    }
                  >
                    {tile.image && (
                      <div className={styles.imageContainer}>
                        <CMSImage
                          image={tile.image}
                          priority={i === 0 ? priority : undefined}
                          loading={i !== 0 && priority ? 'eager' : undefined}
                        />
                      </div>
                    )}
                    <div
                      className={cx(styles.contentContainer, {
                        [styles.imageOverlapContentImageRight]:
                          isImageOverlap && image_alignment === 'Right',
                        [styles.imageOverlapContentImageLeft]:
                          isImageOverlap && image_alignment !== 'Right',
                      })}
                    >
                      {tile.heading && <h3 className={styles.tileHeading}>{tile.heading}</h3>}
                      {tile.description && (
                        <p className={styles.tileDescription}>{tile.description}</p>
                      )}
                    </div>
                  </div>
                )}
                {tile.legal && (
                  <div className={styles.legalText}>
                    <Legal
                      content={{
                        ...mapContentstackLegalValues(tile.legal),
                      }}
                      isQuotient={false}
                      isCMSSuperDuperHero={false}
                      singleLine
                    />
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Grid>
  );
}
